import React, { useState, useEffect, useRef } from 'react';
import { makeStyles } from '@mui/styles';
import { Box, Typography } from '@mui/material';
import { scrollToElementById } from 'utils';

import ScoreBar from './ScoreBar';
import Proctorship from './Proctorship';
import InfoIconWithTooltip from 'components/InfoIconWithTooltip';

const useStyles = makeStyles((theme) => ({
  parentContainer: {
    background: 'linear-gradient(284.04deg, rgba(190, 147, 197, 0.5) 0%, rgba(123, 198, 204, 0.5) 100%)',
    padding: 2,
    borderRadius: theme.spacing(2),
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    boxShadow: `0px 4px 4px 0px #CCD4DE1A,
      0px 10px 20px 0px #CCD4DE4D,
      0px 7px 28px 0px #CCD4DE33`,
    backgroundColor: theme.palette.shades['clr-white-900'],
    borderRadius: theme.spacing(2),
  },
  scoreComponent: {
    display: 'flex',
    justifyContent: "space-between",
    padding: theme.spacing(4.5),
    boxShadow: `0px 4px 4px 0px #CCD4DE1A,
    0px 10px 20px 0px #CCD4DE4D,
    0px 7px 28px 0px #CCD4DE33`,
    width: '100%'
  },
  description: {
    padding: theme.spacing(8, 4)
  },
  clampText: {
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  visuallyHidden: {
    position: 'absolute',
    visibility: 'hidden',
    height: 'auto',
    whiteSpace: 'normal',
    pointerEvents: 'none',
    zIndex: -1,
  },
  bottomContent: {
    padding: theme.spacing(4),
    backgroundColor: theme.palette.success['clr-100'],
    borderRadius : theme.spacing(0 , 0 , 2 , 2)
  }
}));

const getFeedbackByScore = (score, maxScore = 5) => {
  if (maxScore === 10) {
    // Case master feedback
    switch (true) {
      case (score >= 8):
        return "Excellent performance! Your case interview skills demonstrate strong analytical thinking and clear communication.";
      case (score >= 6):
        return "Good work! Continue refining your framework development and data analysis skills to reach the next level.";
      case (score >= 4):
        return "You're making progress! Focus on structuring your approach and improving hypothesis testing.";
      case (score >= 2):
        return "Keep practicing! Work on case introduction and developing logical frameworks.";
      default:
        return "Let's start with the fundamentals — focus on understanding case interview basics first";
    }
  } else {
    // Coding feedback (original)
    switch (true) {
      case (score >= 4):
        return "Almost at the top — deepen your problem analysis and polish your delivery to stand out";
      case (score >= 3):
        return "Good momentum — hone your precision to ensure each solution is both correct and concise";
      case (score >= 2):
        return "Your fundamentals are coming together — now organize your approach and communicate your reasoning clearly";
      case (score >= 1):
        return "You're getting warmed up — consistent practice will steadily strengthen your basics!";
      default:
        return "Let's start with the fundamentals — focus on understanding key concepts first";
    }
  }
}

const Score = ({ 
  score, 
  proctorRecords, 
  maxScore = 5, 
  scoreLabel = "My 4C score:", 
  tooltipTitle = "Score based on 4Cs : Coding, Core Concepts, Critical thinking & Communication.",
  scrollTarget = "Detailed 4Cs feedback",
  bottomText = "Your 4C feedback breaks it down with simple, actionable tips just for you.",
  showDescription = true,
  showBottomContent = true
}) => {
  const classes = useStyles();
  const textRef = useRef();

  const getCatIndex = React.useCallback((score) => {
    if (maxScore === 10) {
      // For case-master (1-10 scale), map to cat GIFs 1-10
      const clamped = Math.min(Math.max(score, 1), 10);
      return Math.floor(clamped);
    } else {
      // For coding (1-5 scale), use original logic
      const clamped = Math.min(score, 5.0);
      if (clamped === 5.0) return 5;
      return Math.floor(clamped);
    }
  }, [maxScore]);

  const handleScroll = () => {
    scrollToElementById(scrollTarget, 'start');
  }

  return (
    <Box className={classes.parentContainer}>
      <Box className={classes.container}>
        <Box className={classes.scoreComponent}>
          <Box display='flex' flexDirection='column'>
            <Box display='flex' alignItems='center' gap='4px'>
              <Typography variant='h4-medium'>{scoreLabel} </Typography>
              <Typography variant='h4-bold' color='primary.clr-400'>{score.toFixed(2)}</Typography>
              <img
                src={`https://assets.languify.in/gifs/cat-${getCatIndex(score)}.gif`}
                alt='cat-gif'
                width={36}
                style={{ marginLeft: '8px' }}
              />
              <InfoIconWithTooltip
                title={tooltipTitle}
                sx={{ fontSize: '35px', marginTop: '2px', marginLeft: '16.5px' }} placement='top'
              />
            </Box>
            <ScoreBar scale={score} maxScale={maxScore} />
          </Box>

          <Proctorship records={proctorRecords} />

        </Box>

        {showDescription && (
          <Box className={classes.description}>
            <Typography
              variant='h6-medium'
              color='neutral.clr-600'
              className={classes.clampText}
              ref={textRef}
            >
              {getFeedbackByScore(parseInt(score), maxScore)}
            </Typography>
          </Box>
        )}

        {showBottomContent && bottomText && (
          <Box className={classes.bottomContent}>
            <Typography variant='h6-medium'>
              ➡️  {bottomText}
            </Typography>&nbsp;
            <Typography
              variant='body01-link'
              color='primary.clr-400'
              sx={{ cursor: 'pointer' }}
              onClick={handleScroll}
            >
              Show me
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Score;
